import { type RefObject, useCallback, useState } from "react";
import { toast } from "@/components/ui/toast";
import { HERALD_PDF_EXCLUDE_DATA_ATTR } from "@/lib/chat/heraldChatDomDataAttrs";
import {
  filenameSlugForComponentType,
  filenameTimestamp,
} from "@/lib/utils/aiChatUtils";
import {
  downloadDomElementAsPng,
  HERALD_CHAT_PNG_SCALE_HIGH_RES,
} from "@/lib/utils/heraldChatUtils";

export interface UseExportComponentAsPngDeps {
  /** Subtree to rasterise; the export is a no-op when `current` is `null`. */
  targetRef: RefObject<HTMLElement | null>;
  /** Catalog type in PascalCase (e.g. `AreaChart`); used in the download filename. */
  componentType: string;
}

export interface UseExportComponentAsPngReturn {
  /** Triggers a PNG download of the subtree; toasts on failure. */
  exportAsPng: () => Promise<void>;
  isPending: boolean;
}

/**
 * DOM filter that excludes nodes tagged with `HERALD_PDF_EXCLUDE_DATA_ATTR`
 * (and their descendants) from the export.
 */
const dropPdfExcludedNodes = (node: Node): boolean => {
  if (
    node instanceof Element &&
    node.hasAttribute(HERALD_PDF_EXCLUDE_DATA_ATTR)
  ) {
    return false;
  }
  return true;
};

/**
 * Exports a DOM subtree as a timestamped PNG download for chat-rendered components.
 */
export const useExportComponentAsPng = ({
  targetRef,
  componentType,
}: UseExportComponentAsPngDeps): UseExportComponentAsPngReturn => {
  const [isPending, setIsPending] = useState(false);

  const exportAsPng = useCallback(async () => {
    const node = targetRef.current;
    if (node === null) {
      return;
    }
    setIsPending(true);
    try {
      await downloadDomElementAsPng(node, {
        scale: HERALD_CHAT_PNG_SCALE_HIGH_RES,
        filename: `chat-${filenameSlugForComponentType(componentType)}-${filenameTimestamp()}.png`,
        backgroundColor: "#ffffff",
        filter: dropPdfExcludedNodes,
      });
    } catch (err) {
      console.error("[useExportComponentAsPng] PNG export failed", err);
      toast.error("Could not export image");
    } finally {
      setIsPending(false);
    }
  }, [targetRef, componentType]);

  return { exportAsPng, isPending };
};
